import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { X, SlidersHorizontal, User, Users, CreditCard, ShieldCheck, LogOut, Trash2, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const TABS = [
  { section: "SETTINGS", items: [
    { id: "general", label: "General", icon: SlidersHorizontal },
  ]},
  { section: "ACCOUNT", items: [
    { id: "account", label: "Account", icon: User },
    { id: "team", label: "Team", icon: Users },
    { id: "plans", label: "Plans and Billing", icon: CreditCard },
    { id: "privacy", label: "Data and Privacy", icon: ShieldCheck },
  ]},
];

function GeneralTab() {
  return (
    <div>
      <h2 className="text-2xl font-semibold font-['Manrope'] mb-1">General</h2>
      <div className="border-b border-gray-200 mb-6" />
      <div className="space-y-0 bg-amber-50/50 rounded-lg border border-amber-100">
        <div className="flex items-center justify-between p-5 border-b border-amber-100/60">
          <div>
            <p className="text-sm font-medium text-gray-800">Default PPT Engine</p>
            <p className="text-xs text-gray-500 mt-0.5">Choose between speed and quality for slide generation</p>
          </div>
          <span className="text-sm text-gray-600 font-medium">Advanced (Quality)</span>
        </div>
        <div className="flex items-center justify-between p-5 border-b border-amber-100/60">
          <div>
            <p className="text-sm font-medium text-gray-800">Default Mode</p>
            <p className="text-xs text-gray-500 mt-0.5">Start in single slide or multi-slide deck mode</p>
          </div>
          <span className="text-sm text-gray-600 font-medium">Multi-slide Deck</span>
        </div>
        <div className="flex items-center justify-between p-5">
          <div>
            <p className="text-sm font-medium text-gray-800">Auto-save Drafts</p>
            <p className="text-xs text-gray-500 mt-0.5">Automatically save your work as you type</p>
          </div>
          <Switch defaultChecked data-testid="auto-save-toggle" />
        </div>
      </div>
    </div>
  );
}

function AccountTab({ user, logout, onClose }) {
  const [firstName, setFirstName] = useState(user?.name?.split(" ")[0] || "");
  const [lastName, setLastName] = useState(user?.name?.split(" ").slice(1).join(" ") || "");
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        name: `${firstName} ${lastName}`.trim(),
      }, { withCredentials: true });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPw || newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setChangingPw(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPw || undefined,
        new_password: newPw,
      }, { withCredentials: true });
      toast.success("Password changed");
      setCurrentPw(""); setNewPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPw(false);
    }
  };

  const initial = (user?.name || user?.email || "U")[0].toUpperCase();

  return (
    <div>
      <h2 className="text-2xl font-semibold font-['Manrope'] mb-1">Account</h2>
      <div className="border-b border-gray-200 mb-6" />

      <div className="space-y-0 bg-amber-50/50 rounded-lg border border-amber-100">
        <div className="flex items-center justify-between p-5 border-b border-amber-100/60">
          <span className="text-sm font-medium text-gray-800">First name</span>
          <Input value={firstName} onChange={e => setFirstName(e.target.value)} className="w-48 h-9 text-sm bg-white" data-testid="settings-first-name" />
        </div>
        <div className="flex items-center justify-between p-5 border-b border-amber-100/60">
          <span className="text-sm font-medium text-gray-800">Last name</span>
          <Input value={lastName} onChange={e => setLastName(e.target.value)} className="w-48 h-9 text-sm bg-white" data-testid="settings-last-name" />
        </div>
        <div className="flex items-center justify-between p-5 border-b border-amber-100/60">
          <span className="text-sm font-medium text-gray-800">Email</span>
          <span className="text-sm text-gray-600">{user?.email}</span>
        </div>
        <div className="flex items-center justify-between p-5">
          <span className="text-sm font-medium text-gray-800">Profile picture</span>
          <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-white text-sm font-bold">{initial}</div>
        </div>
      </div>

      {/* Change Password */}
      <div className="mt-6 space-y-0 bg-amber-50/50 rounded-lg border border-amber-100">
        <div className="flex items-center justify-between p-5 border-b border-amber-100/60">
          <span className="text-sm font-medium text-gray-800">Current password</span>
          <div className="relative w-48">
            <Input type={showCurrentPw ? "text" : "password"} value={currentPw} onChange={e => setCurrentPw(e.target.value)} className="h-9 text-sm bg-white pr-9" data-testid="settings-current-pw" />
            <button onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showCurrentPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between p-5">
          <span className="text-sm font-medium text-gray-800">New password</span>
          <div className="relative w-48">
            <Input type={showNewPw ? "text" : "password"} value={newPw} onChange={e => setNewPw(e.target.value)} className="h-9 text-sm bg-white pr-9" data-testid="settings-new-pw" />
            <button onClick={() => setShowNewPw(!showNewPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showNewPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" className="h-9 text-sm" onClick={() => { logout(); onClose(); }} data-testid="settings-signout">
            <LogOut className="w-3.5 h-3.5 mr-1.5" />Sign out
          </Button>
          <button className="text-sm text-gray-500 hover:text-red-600 transition-colors" data-testid="settings-delete-account">Delete account</button>
        </div>
        <div className="flex items-center gap-2">
          {newPw && (
            <Button variant="outline" className="h-9 text-sm" onClick={handleChangePassword} disabled={changingPw}>
              {changingPw ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}Change Password
            </Button>
          )}
          <Button className="h-9 text-sm bg-gray-800 hover:bg-gray-900" onClick={handleSave} disabled={saving} data-testid="settings-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function TeamTab() {
  const [emails, setEmails] = useState(["", "", ""]);

  const updateEmail = (idx, val) => {
    setEmails(prev => { const n = [...prev]; n[idx] = val; return n; });
  };

  const addRow = () => setEmails(prev => [...prev, ""]);

  return (
    <div>
      <h2 className="text-2xl font-semibold font-['Manrope'] mb-1">Team</h2>
      <div className="border-b border-gray-200 mb-6" />

      <div className="text-center py-4">
        <p className="text-sm text-gray-500 mb-1">Unlock shared templates, unified billing, admin controls and more</p>
        <h3 className="text-lg font-semibold font-['Manrope'] mb-6">Invite your teammates</h3>

        <div className="max-w-md mx-auto space-y-3">
          {emails.map((email, idx) => (
            <Input
              key={idx}
              type="email"
              placeholder={`teammate${idx + 1}@wokelo.ai`}
              value={email}
              onChange={e => updateEmail(idx, e.target.value)}
              className="h-11 text-sm bg-amber-50/50 border-amber-100"
              data-testid={`team-email-${idx + 1}`}
            />
          ))}
          <button onClick={addRow} className="text-xs text-gray-500 hover:text-gray-700">+ Add another</button>
        </div>

        <p className="text-xs text-gray-400 mt-4 mb-5">You can also add team members after team creation</p>

        <Button className="h-11 px-8 text-sm bg-gray-800 hover:bg-gray-900" data-testid="team-continue-btn">
          Continue
        </Button>
      </div>

      <div className="flex items-center justify-between mt-10 pt-4 border-t border-gray-200">
        <button className="text-xs text-gray-500 hover:text-gray-700 underline">Teams FAQ</button>
        <button className="text-xs text-gray-500 hover:text-gray-700">More questions about teams? <span className="underline">Contact us</span></button>
      </div>
    </div>
  );
}

function PlansTab() {
  return (
    <div>
      <h2 className="text-2xl font-semibold font-['Manrope'] mb-1">Plans and Billing</h2>
      <div className="border-b border-gray-200 mb-6" />

      <div className="bg-amber-50/50 rounded-lg border border-amber-100 p-5 mb-4">
        <p className="text-sm font-semibold text-gray-800">Wokelo Pro</p>
        <p className="text-xs text-gray-500 mt-0.5">Usage-based pricing per slide generation</p>
        <Button variant="outline" className="h-9 text-sm mt-3" data-testid="explore-features-btn">Explore features</Button>
        <span className="text-xs text-gray-400 ml-3">Manage subscription in admin panel</span>
      </div>

      <div className="bg-amber-50/50 rounded-lg border border-amber-100 p-5">
        <p className="text-sm font-semibold text-gray-800">Use Wokelo with your team</p>
        <p className="text-xs text-gray-500 mt-0.5">Unlock shared templates, snippets, and more</p>
        <Button variant="outline" className="h-9 text-sm mt-3" data-testid="invite-teammates-btn">Invite teammates</Button>
      </div>

      <p className="text-xs text-gray-400 mt-6">Want Wokelo for your company? <button className="underline hover:text-gray-600">Contact sales</button></p>

      <div className="flex items-center justify-between mt-8 pt-4 border-t border-gray-200">
        <button className="text-xs text-gray-500 hover:text-gray-700 underline">Billing and Plans FAQ</button>
        <button className="text-xs text-gray-500 hover:text-gray-700">Questions about billing? <span className="underline">Contact us</span></button>
      </div>
    </div>
  );
}

function PrivacyTab() {
  const [privacyMode, setPrivacyMode] = useState(false);
  const [contextAware, setContextAware] = useState(true);

  return (
    <div>
      <h2 className="text-2xl font-semibold font-['Manrope'] mb-1">Data and Privacy</h2>
      <div className="border-b border-gray-200 mb-6" />

      <div className="space-y-0 bg-amber-50/50 rounded-lg border border-amber-100">
        <div className="flex items-start justify-between p-5 border-b border-amber-100/60">
          <div className="flex-1 mr-4">
            <p className="text-sm font-medium text-gray-800">Privacy Mode</p>
            <p className="text-xs text-gray-500 mt-0.5">If enabled, none of your generation data will be stored or used for model training by us or any third party (zero data retention).</p>
          </div>
          <Switch checked={privacyMode} onCheckedChange={setPrivacyMode} data-testid="privacy-mode-toggle" />
        </div>
        <div className="flex items-start justify-between p-5 border-b border-amber-100/60">
          <div className="flex-1 mr-4">
            <p className="text-sm font-medium text-gray-800">Context awareness</p>
            <p className="text-xs text-gray-500 mt-0.5">Allow Wokelo to use your uploaded content to better understand context and improve slide generation quality.</p>
          </div>
          <Switch checked={contextAware} onCheckedChange={setContextAware} data-testid="context-aware-toggle" />
        </div>
        <div className="flex items-start justify-between p-5 border-b border-amber-100/60">
          <div className="flex-1 mr-4">
            <p className="text-sm font-medium text-gray-800">Data storage</p>
            <p className="text-xs text-gray-500 mt-0.5">Control how your generated slides are stored.</p>
          </div>
          <span className="text-sm text-gray-600 whitespace-nowrap">Store data normally</span>
        </div>
        <div className="flex items-start justify-between p-5">
          <div className="flex-1 mr-4">
            <p className="text-sm font-medium text-gray-800">Export all data</p>
            <p className="text-xs text-gray-500 mt-0.5">Download a copy of all your generated slides and account data.</p>
          </div>
          <Button variant="outline" className="h-8 text-xs" data-testid="export-data-btn">Export</Button>
        </div>
      </div>
    </div>
  );
}

export default function SettingsModal({ open, onClose }) {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("account");

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="settings-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-[720px] max-w-[95vw] max-h-[85vh] flex overflow-hidden" data-testid="settings-modal-content">
        {/* Left sidebar */}
        <div className="w-56 flex-shrink-0 bg-gray-50 border-r border-gray-200 flex flex-col">
          <div className="flex-1 py-5 px-3">
            {TABS.map(group => (
              <div key={group.section} className="mb-4">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 mb-2">{group.section}</p>
                {group.items.map(item => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors mb-0.5 ${
                        isActive ? "bg-amber-50 text-gray-900 font-medium" : "text-gray-600 hover:bg-gray-100"
                      }`}
                      data-testid={`settings-tab-${item.id}`}
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="px-4 py-3 border-t border-gray-200">
            <p className="text-[10px] text-gray-400">Wokelo v1.0</p>
          </div>
        </div>

        {/* Right content */}
        <div className="flex-1 overflow-y-auto p-6 relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-testid="settings-modal-close">
            <X className="w-4 h-4" />
          </button>

          {activeTab === "general" && <GeneralTab />}
          {activeTab === "account" && <AccountTab user={user} logout={logout} onClose={onClose} />}
          {activeTab === "team" && <TeamTab />}
          {activeTab === "plans" && <PlansTab />}
          {activeTab === "privacy" && <PrivacyTab />}
        </div>
      </div>
    </div>
  );
}
